import { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Shield, Plus, Trash2, Save, Loader2, Search, ChevronUp, ChevronDown, X, Lock } from 'lucide-react';
import { apiService, PanelCatalogItem, PanelGradeDTO, PanelUserDTO, LuckPermsGroupDTO } from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { GAMES } from '@/config/games';
import styles from './AdminPage.module.scss';

interface Catalog { projects: PanelCatalogItem[]; pages: PanelCatalogItem[]; }

/** Espace Administration : gestion des utilisateurs (attribution de grade) + des grades (rôles panel). */
export function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user?.isAdmin;
  const [tab, setTab] = useState<'users' | 'roles' | 'protected'>('users');
  const [catalog, setCatalog] = useState<Catalog>({ projects: [], pages: [] });
  const [grades, setGrades] = useState<PanelGradeDTO[]>([]);
  const [users, setUsers] = useState<PanelUserDTO[]>([]);
  const [loading, setLoading] = useState(false);

  // Les non-admins (gestion déléguée) n'ont pas l'onglet Grades ni le catalogue → on ne l'appelle pas.
  const load = useCallback(async () => {
    setLoading(true);
    const [g, u, c] = await Promise.all([
      apiService.getPanelGrades(),
      apiService.getPanelUsers(),
      isAdmin ? apiService.getPanelCatalog() : Promise.resolve({ data: undefined }),
    ]);
    setGrades(g.data?.grades ?? []);
    setUsers(u.data?.users ?? []);
    if (c.data) setCatalog(c.data);
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!isAdmin && tab !== 'users') setTab('users'); }, [isAdmin, tab]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Administration</h1>
        <p className={styles.subtitle}>Gère les utilisateurs du panel et leurs grades (rôles &amp; permissions par projet).</p>
      </header>

      <div className={styles.tabBar}>
        <button className={`${styles.tab} ${tab === 'users' ? styles.tabActive : ''}`} onClick={() => setTab('users')}>
          <Users size={15} /> Utilisateurs ({users.length})
        </button>
        {isAdmin && (
          <button className={`${styles.tab} ${tab === 'roles' ? styles.tabActive : ''}`} onClick={() => setTab('roles')}>
            <Shield size={15} /> Grades ({grades.length})
          </button>
        )}
        {isAdmin && (
          <button className={`${styles.tab} ${tab === 'protected' ? styles.tabActive : ''}`} onClick={() => setTab('protected')}>
            <Lock size={15} /> Grades protégés
          </button>
        )}
      </div>

      {loading ? (
        <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>
      ) : tab === 'users' ? (
        <UsersTab
          users={users}
          grades={grades}
          isAdmin={isAdmin}
          canAssign={!!user?.canAssignGrades}
          assignable={user?.assignableGradeIds ?? []}
          onReload={load}
        />
      ) : tab === 'protected' ? (
        <ProtectedGradesTab />
      ) : (
        <RolesTab grades={grades} catalog={catalog} onReload={load} />
      )}
    </div>
  );
}

function timeAgo(ts: number): string {
  if (!ts) return '—';
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60); if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.floor(h / 24)} j`;
}

function UsersTab({ users, grades, isAdmin, canAssign, assignable, onReload }: {
  users: PanelUserDTO[]; grades: PanelGradeDTO[];
  isAdmin: boolean; canAssign: boolean; assignable: string[];
  onReload: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState<string | null>(null); // sub du user dont le menu « + » est ouvert
  const [projectTab, setProjectTab] = useState<string>('all'); // 'all' ou un id de projet (paladium/nationsglory/recube)

  // Le caller peut-il attribuer/retirer ce grade ? admin partout, sinon limité à sa liste blanche.
  const canToggle = (gradeId: string) => isAdmin || (canAssign && assignable.includes(gradeId));

  const toggleGrade = async (u: PanelUserDTO, gradeId: string) => {
    if (!canToggle(gradeId)) return;
    const current = u.gradeIds ?? [];
    const next = current.includes(gradeId) ? current.filter((g) => g !== gradeId) : [...current, gradeId];
    setBusy(u.sub);
    await apiService.setPanelUserGrades(u.sub, next);
    setBusy(null);
    onReload();
  };

  const gradeById = useMemo(() => new Map(grades.map((g) => [g.id, g] as const)), [grades]);

  // Projets où le user a AU MOINS une permission (admin = tous). Sert au filtrage par onglet projet.
  const projectsOf = (u: PanelUserDTO): Set<string> => {
    if (u.admin) return new Set(GAMES.map((g) => g.id));
    const s = new Set<string>();
    (u.gradeIds ?? []).forEach((id) => {
      const g = gradeById.get(id);
      (g?.permissions ?? []).forEach((p) => { const proj = p.split('.')[0]; if (proj) s.add(proj); });
    });
    return s;
  };

  // Grade PRÉDOMINANT = le plus haut dans la hiérarchie (grades est déjà trié admin d'abord, puis rang).
  const primaryGradeId = (u: PanelUserDTO): string | null => {
    const owned = new Set(u.gradeIds ?? []);
    return grades.find((g) => owned.has(g.id))?.id ?? null;
  };

  const projectTabs = useMemo(() => [{ key: 'all', label: 'Tous' }, ...GAMES.map((g) => ({ key: g.id, label: g.name }))], []);

  // Users visibles : recherche (pseudo/email) + onglet projet actif.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = users;
    if (q) list = list.filter((u) => (u.handle ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q));
    if (projectTab !== 'all') list = list.filter((u) => projectsOf(u).has(projectTab));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, query, projectTab, grades]);

  // Regroupement par grade prédominant (ordre hiérarchique), puis « Sans grade » à la fin.
  const sections = useMemo(() => {
    const byGrade = new Map<string, PanelUserDTO[]>();
    const noGrade: PanelUserDTO[] = [];
    visible.forEach((u) => {
      const pid = primaryGradeId(u);
      if (!pid) { noGrade.push(u); return; }
      if (!byGrade.has(pid)) byGrade.set(pid, []);
      byGrade.get(pid)!.push(u);
    });
    const out: { key: string; label: string; color?: string; users: PanelUserDTO[] }[] = [];
    grades.forEach((g) => {
      const us = byGrade.get(g.id);
      if (us && us.length) out.push({ key: g.id, label: `${g.name}${g.admin ? ' (admin)' : ''}`, color: g.color, users: us });
    });
    if (noGrade.length) out.push({ key: '__none__', label: 'Sans grade', users: noGrade });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, grades]);

  const renderUser = (u: PanelUserDTO) => {
    const assigned = u.gradeIds ?? [];
    // Grades possédés (dans l'ordre hiérarchique) et grades ajoutables (non possédés + attribuables par le caller).
    const owned = grades.filter((g) => assigned.includes(g.id));
    const addable = grades.filter((g) => !assigned.includes(g.id) && canToggle(g.id));
    return (
      <div key={u.sub} className={styles.userRow}>
        <div className={styles.userAvatar}>
          {u.avatar ? <img src={u.avatar} alt={u.handle} /> : <span>{u.handle?.charAt(0).toUpperCase() ?? '?'}</span>}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>{u.handle}</div>
          <div className={styles.userMeta}>{u.email ?? '—'} · connexion {timeAgo(u.lastLogin)}</div>
        </div>
        <div className={styles.chipRow}>
          {owned.length === 0 && <span className={styles.noGrade}>aucun grade — pas d'accès</span>}
          {owned.map((g) => {
            const removable = canToggle(g.id);
            return (
              <span key={g.id} className={styles.assignedChip} title={g.admin ? 'Grade admin' : g.name}>
                <span className={styles.chipDot} style={{ background: g.color }} />
                {g.name}{g.admin ? ' (admin)' : ''}
                {removable && (
                  <button
                    type="button"
                    className={styles.chipRemove}
                    disabled={busy === u.sub}
                    onClick={() => toggleGrade(u, g.id)}
                    title="Retirer ce grade"
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
          {addable.length > 0 && (
            <span className={styles.addWrap}>
              <button
                type="button"
                className={styles.addBtn}
                disabled={busy === u.sub}
                onClick={() => setAddOpen(addOpen === u.sub ? null : u.sub)}
                title="Ajouter un grade"
              >
                <Plus size={14} />
              </button>
              {addOpen === u.sub && (
                <>
                  <div className={styles.addOverlay} onClick={() => setAddOpen(null)} />
                  <ul className={styles.addMenu}>
                    {addable.map((g) => (
                      <li key={g.id}>
                        <button
                          type="button"
                          className={styles.addMenuItem}
                          onClick={() => { setAddOpen(null); toggleGrade(u, g.id); }}
                        >
                          <span className={styles.chipDot} style={{ background: g.color }} />
                          {g.name}{g.admin ? ' (admin)' : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (users.length === 0) return <p className={styles.muted}>Aucun utilisateur connecté pour l'instant.</p>;

  return (
    <div>
      <div className={styles.projectTabs}>
        {projectTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.projectTab} ${projectTab === t.key ? styles.projectTabActive : ''}`}
            onClick={() => setProjectTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ position: 'relative' }}>
        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, opacity: 0.5 }} />
        <input
          className={styles.searchInput}
          style={{ paddingLeft: 32 }}
          placeholder="Rechercher un utilisateur (pseudo, email)…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {sections.length === 0 ? (
        <p className={styles.muted}>
          {query ? `Aucun utilisateur ne correspond à « ${query} ».` : 'Aucun utilisateur dans ce projet.'}
        </p>
      ) : (
        sections.map((sec) => (
          <div key={sec.key}>
            <div className={styles.groupTitle}>
              {sec.color && <span className={styles.chipDot} style={{ background: sec.color, marginRight: 6 }} />}
              {sec.label} ({sec.users.length})
            </div>
            <div className={styles.list}>{sec.users.map(renderUser)}</div>
          </div>
        ))
      )}
    </div>
  );
}

/** Onglet Grades protégés (admin) : choisir les grades in-game éditables par admin uniquement. */
function ProtectedGradesTab() {
  const [groups, setGroups] = useState<LuckPermsGroupDTO[]>([]);
  const [protectedSet, setProtectedSet] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await apiService.getAllGroups();
    const gs = (res.data?.groups ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
    setGroups(gs);
    setProtectedSet(new Set((res.data?.protectedGrades ?? []).map((n) => n.toLowerCase())));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (name: string) => {
    const key = name.toLowerCase();
    if (key === 'admin') return; // toujours protégé, non modifiable
    const next = new Set(protectedSet);
    if (next.has(key)) next.delete(key); else next.add(key);
    setProtectedSet(next);
    setSaving(true);
    await apiService.setProtectedGrades([...next].filter((n) => n !== 'admin'));
    setSaving(false);
    load();
  };

  if (loading) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /> Chargement…</div>;

  return (
    <div>
      <p className={styles.muted} style={{ marginBottom: 12 }}>
        Coche les grades in-game qui ne peuvent être édités (permissions, chat, boutique, apparence, suppression)
        <strong> que par un administrateur du panel</strong> — à l'image du grade <code>admin</code> de LuckPerms.
        Un staff avec l'accès aux Grades pourra les voir mais pas les modifier.
      </p>
      {groups.length === 0 ? (
        <p className={styles.muted}>Aucun grade in-game trouvé.</p>
      ) : (
        <div className={styles.list}>
          {groups.map((g) => {
            const key = g.name.toLowerCase();
            const always = key === 'admin';
            return (
              <label key={g.name} className={styles.pageRow}>
                <input
                  type="checkbox"
                  checked={protectedSet.has(key)}
                  disabled={always || saving}
                  onChange={() => toggle(g.name)}
                />
                <Lock size={13} style={{ opacity: protectedSet.has(key) ? 0.9 : 0.3 }} />
                {g.name}{always && <span className={styles.muted} style={{ fontSize: 12 }}> — toujours protégé</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface Draft {
  id?: string; name: string; color: string; admin: boolean; permissions: Set<string>;
  manageAccess: boolean; canAssignGrades: boolean; assignableGradeIds: Set<string>;
}

function RolesTab({ grades, catalog, onReload }: { grades: PanelGradeDTO[]; catalog: Catalog; onReload: () => void }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [project, setProject] = useState('');
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Réordonnancement « à la Discord » : on déplace un grade parmi ses pairs. Les grades admin restent
  // forcément au-dessus des non-admins → on interdit de franchir la frontière admin/non-admin.
  const canUp = (i: number) => i > 0 && grades[i - 1]!.admin === grades[i]!.admin;
  const canDown = (i: number) => i < grades.length - 1 && grades[i + 1]!.admin === grades[i]!.admin;

  const move = async (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= grades.length) return;
    if (grades[j]!.admin !== grades[i]!.admin) return;
    const order = grades.map((g) => g.id);
    [order[i], order[j]] = [order[j]!, order[i]!];
    setReordering(true);
    await apiService.reorderPanelGrades(order);
    setReordering(false);
    onReload();
  };

  useEffect(() => {
    if (!project && catalog.projects.length > 0) setProject(catalog.projects[0]!.key);
  }, [catalog, project]);

  const edit = (g: PanelGradeDTO) => setDraft({
    id: g.id, name: g.name, color: g.color, admin: g.admin, permissions: new Set(g.permissions),
    manageAccess: !!g.manageAccess, canAssignGrades: !!g.canAssignGrades,
    assignableGradeIds: new Set(g.assignableGradeIds ?? []),
  });
  const create = () => setDraft({
    name: '', color: '#8B3FF1', admin: false, permissions: new Set(),
    manageAccess: false, canAssignGrades: false, assignableGradeIds: new Set(),
  });

  // Pages regroupées par famille (ordre du catalogue préservé).
  const families = useMemo(() => {
    const map = new Map<string, PanelCatalogItem[]>();
    catalog.pages.forEach((p) => {
      const fam = p.group ?? 'Autres';
      if (!map.has(fam)) map.set(fam, []);
      map.get(fam)!.push(p);
    });
    return [...map.entries()];
  }, [catalog.pages]);

  const togglePage = (pageKey: string) => {
    if (!draft) return;
    const key = `${project}.${pageKey}`;
    const next = new Set(draft.permissions);
    if (next.has(key)) next.delete(key); else next.add(key);
    setDraft({ ...draft, permissions: next });
  };

  const toggleProjectAll = (on: boolean) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    catalog.pages.forEach((p) => { const k = `${project}.${p.key}`; if (on) next.add(k); else next.delete(k); });
    setDraft({ ...draft, permissions: next });
  };

  const toggleFamily = (pages: PanelCatalogItem[], on: boolean) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    pages.forEach((p) => { const k = `${project}.${p.key}`; if (on) next.add(k); else next.delete(k); });
    setDraft({ ...draft, permissions: next });
  };

  const toggleAssignable = (gradeId: string) => {
    if (!draft) return;
    const next = new Set(draft.assignableGradeIds);
    if (next.has(gradeId)) next.delete(gradeId); else next.add(gradeId);
    setDraft({ ...draft, assignableGradeIds: next });
  };

  const save = async () => {
    if (!draft || !draft.name.trim()) return;
    setSaving(true);
    await apiService.savePanelGrade({
      id: draft.id, name: draft.name.trim(), color: draft.color, admin: draft.admin,
      permissions: [...draft.permissions],
      manageAccess: draft.manageAccess, canAssignGrades: draft.canAssignGrades,
      assignableGradeIds: [...draft.assignableGradeIds],
    });
    setSaving(false);
    setDraft(null);
    onReload();
  };

  const remove = async () => {
    if (!draft?.id) { setDraft(null); return; }
    if (!window.confirm(`Supprimer le grade « ${draft.name} » ?`)) return;
    setSaving(true);
    await apiService.deletePanelGrade(draft.id);
    setSaving(false);
    setDraft(null);
    onReload();
  };

  return (
    <div className={styles.rolesLayout}>
      <div className={styles.gradeList}>
        <button className={styles.newBtn} onClick={create}><Plus size={14} /> Nouveau grade</button>
        {grades.map((g, i) => (
          <div key={g.id} className={styles.gradeItemRow}>
            <button
              className={`${styles.gradeItem} ${draft?.id === g.id ? styles.gradeItemActive : ''}`}
              style={{ flex: 1 }}
              onClick={() => edit(g)}
            >
              <span className={styles.gradeDot} style={{ background: g.color }} />
              {g.name}{g.admin && <span className={styles.adminTag}>admin</span>}
            </button>
            <div className={styles.gradeMove}>
              <button className={styles.moveBtn} title="Monter" disabled={reordering || !canUp(i)} onClick={() => move(i, -1)}>
                <ChevronUp size={12} />
              </button>
              <button className={styles.moveBtn} title="Descendre" disabled={reordering || !canDown(i)} onClick={() => move(i, 1)}>
                <ChevronDown size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.gradeEditor}>
        {!draft ? (
          <p className={styles.muted}>Sélectionne un grade à éditer ou crée-en un.</p>
        ) : (
          <>
            <div className={styles.field}>
              <label>Nom</label>
              <input className={styles.input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="ex. Modérateur" />
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label>Couleur</label>
                <input type="color" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })} />
              </div>
              <label className={styles.adminToggle}>
                <input type="checkbox" checked={draft.admin} onChange={(e) => setDraft({ ...draft, admin: e.target.checked })} />
                Admin (accès total + gestion complète users/grades)
              </label>
            </div>

            {!draft.admin && (
              <>
                <div className={styles.delegation}>
                  <span className={styles.delegationTitle}>Gestion des accès</span>
                  <label className={styles.adminToggle}>
                    <input
                      type="checkbox"
                      checked={draft.manageAccess}
                      onChange={(e) => setDraft({ ...draft, manageAccess: e.target.checked })}
                    />
                    Peut accéder à « Grades &amp; accès » (voir les utilisateurs et grades)
                  </label>
                  <label className={styles.adminToggle}>
                    <input
                      type="checkbox"
                      checked={draft.canAssignGrades}
                      onChange={(e) => setDraft({ ...draft, canAssignGrades: e.target.checked })}
                    />
                    Peut attribuer des grades aux utilisateurs
                  </label>
                  {draft.canAssignGrades && (
                    <div className={styles.assignList}>
                      <span className={styles.muted} style={{ fontSize: 12 }}>Grades qu'il peut attribuer :</span>
                      {grades.filter((g) => g.id !== draft.id).length === 0 && (
                        <span className={styles.muted} style={{ fontSize: 12 }}>Aucun autre grade à attribuer.</span>
                      )}
                      {grades.filter((g) => g.id !== draft.id).map((g) => (
                        <label key={g.id} className={styles.pageRow}>
                          <input
                            type="checkbox"
                            checked={draft.assignableGradeIds.has(g.id)}
                            onChange={() => toggleAssignable(g.id)}
                          />
                          <span className={styles.chipDot} style={{ background: g.color }} />
                          {g.name}{g.admin ? ' (admin)' : ''}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.matrix}>
                  <div className={styles.projectTabs}>
                    {catalog.projects.map((p) => (
                      <button key={p.key} className={`${styles.projectTab} ${project === p.key ? styles.projectTabActive : ''}`} onClick={() => setProject(p.key)}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <label className={styles.pageRow} style={{ fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={catalog.pages.length > 0 && catalog.pages.every((p) => draft.permissions.has(`${project}.${p.key}`))}
                      onChange={(e) => toggleProjectAll(e.target.checked)}
                    />
                    Tout {catalog.projects.find((p) => p.key === project)?.label}
                  </label>
                  {families.map(([fam, pages]) => (
                    <div key={fam}>
                      <div className={styles.familyHeader}>
                        {fam}
                        <label className={styles.familyAll}>
                          <input
                            type="checkbox"
                            checked={pages.length > 0 && pages.every((p) => draft.permissions.has(`${project}.${p.key}`))}
                            onChange={(e) => toggleFamily(pages, e.target.checked)}
                          />
                          tout
                        </label>
                      </div>
                      {pages.map((p) => (
                        <label key={p.key} className={styles.pageRow}>
                          <input type="checkbox" checked={draft.permissions.has(`${project}.${p.key}`)} onChange={() => togglePage(p.key)} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={styles.actions}>
              <button className={styles.saveBtn} onClick={save} disabled={saving || !draft.name.trim()}>
                {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
              </button>
              <button className={styles.deleteBtn} onClick={remove} disabled={saving}>
                <Trash2 size={14} /> {draft.id ? 'Supprimer' : 'Annuler'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
